import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from "styled-components";
import { getOfficeLayout } from "@features/desks/services/DeskBooking";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { OfficeDeskSpotOverlay } from "./floor-map-view/OfficeDeskSpotOverlay";
import Colours from '@shared/ui/atoms/Colours';
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import { BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import { useDispatch, useSelector } from "react-redux";
import {
  selectedOffice,
  selectIsOfficeListLoading,
  selectOfficeFloorPlan,
  selectSelectedArea,
  setOfficeFloorPlan
} from "../../../store/officeView.duck";
import { MapButtonRow, MapControlButton, MapControlsContainer, SelectionMessage } from "./ViewComponents";
import {
  clearDesks,
  fetchDeskReservations,
  fetchDesks,
  selectAllOfficeDeskReservations,
  selectAllOfficeDesks,
  selectIsDeskBookingLoading
} from "@features/desks/store/deskBooking.duck";
import { selectActiveDay } from "../../../store/editMovements.duck";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { OfficeAreaSpotOverlay } from "./floor-map-view/OfficeAreaSpotOverlay";
import { selectIsOfficeUsageLoading } from "../../../store/officeUsage.duck";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Period } from '@features/whereabouts/models/movements.models';
import { Desk, DeskReservations } from "@features/desks/services/DeskBookingService";
import { loadFavouriteOffices, selectAllOfficeEntities } from "@features/desks/store/advanceHotDeskingSetup.duck";
import { SingleDeskSpotOverlay } from "./floor-map-view/SingleDeskSpotOverlay";
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { getParameterByName } from "@shared/utils/UrlUtils";
import { getInt } from "@shared/utils/MathUtils";
import { MeetingRoomSpotOverlay } from "./floor-map-view/MeetingRoomSpotOverlay";
import {
  addSelectedDeskId,
  clearSelectedDeskIds,
  endDeskMove,
  moveDeskBy,
  endAreaMove,
  moveAreaBy,
  removeSelectedDeskId,
  resizeArea,
  revertAreaMove,
  revertDeskMove,
  selectAreaEdits,
  selectDeskEdits,
  selectDraggingAreaId,
  selectDraggingDeskId,
  selectPendingDeskEditCount,
  selectSelectedDeskIds,
  selectArea,
  selectSidebarMode,
  setMapViewport,
  setSidebarMode,
  startAreaMove,
  startDeskMove
} from "../ducks/manageDesks.duck";
import { SidebarMode } from "../data/ManageDesks.model";
import { warningNotification } from "@shared/store/notification.duck";
import { Permission } from "@shared/models/user.models";
import { IsBetaTester } from '@shared/hooks/IsBetaTester';
import { isCompanyAdminUser } from '@shared/utils/AccessControl';
import { selectMeetingRoomsInOffice } from '@features/settings/pages/meeting-rooms/meeting-rooms-settings.duck';
import { AppState } from '@app/store/state/app.state';
import { useTranslation } from "react-i18next";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { isInTeams } from "@shared/utils/TeamsUtils";
import { OfficeEntity } from "@features/desks/services/AdvanceHotDeskingService";

interface Props {
  onDeskSelect?: (desk: any) => any;
  onOfficeSelect?: (office: any) => any;
  showAdminButtons: boolean;
}

export function getFloorPlanUrl(floorPlanImage: string) {
  if (floorPlanImage.startsWith('s3/')) {
    return `https://team-today-floorplans.s3.eu-west-2.amazonaws.com/${floorPlanImage}`;
  } else {
    return floorPlanImage;
  }
}

export function OfficeFloorMap(props: Props) {
  const {showAdminButtons} = props;
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const [activeSpot, setActiveDeskSpot] = useState<any>(undefined);
  const [activePeriod, setActivePeriod] = useState(Period.AllDay);
  const myRef = useRef(null);

  const officeFloorPlan = useSelector(selectOfficeFloorPlan);
  const currentOffice = useSelector(selectedOffice);
  const selectedDate = useSelector(selectActiveDay);
  const currentUser = useSelector(selectCurrentUser);
  const allOffices = useSelector(selectAllOfficeEntities);
  const desks = useSelector(selectAllOfficeDesks);
  const reservations = useSelector(selectAllOfficeDeskReservations);
  const isDeskBookingLoading = useSelector(selectIsDeskBookingLoading);
  const isOfficeUsageLoading = useSelector(selectIsOfficeUsageLoading);
  const isOfficeViewLoading = useSelector(selectIsOfficeListLoading);
  const selectedArea = useSelector(selectSelectedArea);
  const selectedSidebarMode = useSelector(selectSidebarMode);
  const selectedDesksForManageDesks = useSelector(selectSelectedDeskIds);
  const [hasOfficeFloorPlan, setHasOfficeFloorPlan] = useState(false);
  const meetingRooms = useSelector((state: AppState) => selectMeetingRoomsInOffice(state, currentOffice?.id ?? 0));

  const isManageDesksActive = selectedSidebarMode === SidebarMode.ManageDesks;

  const isBetaTester = IsBetaTester();
  const isDeskMoveEnabled = isManageDesksActive && isBetaTester;
  const deskEdits = useSelector(selectDeskEdits);
  const draggingDeskId = useSelector(selectDraggingDeskId);
  const pendingDeskEditCount = useSelector(selectPendingDeskEditCount);
  const areaEdits = useSelector(selectAreaEdits);
  const draggingAreaId = useSelector(selectDraggingAreaId);
  const dragLastMousePos = useRef({x: 0, y: 0});
  const dragDistanceRef = useRef(0);
  const didDragRef = useRef(false);

  const editFloorPlanButtonEnabled = useMemo(() => {
    if (!showAdminButtons) return false;
    return isCompanyAdminUser(currentUser);
  }, [currentUser, showAdminButtons]);

  const manageDesksButtonEnabled = useMemo(() => {
    if (!showAdminButtons) return false;
    if (currentUser?.role === Permission.CompanyAdmin || currentUser?.role === Permission.OfficeAdmin)  {
      return true;
    }
    return false;
  }, [currentUser, showAdminButtons]);

  const isLoadingRef = useRef(false);
  const isLoading = useMemo(() => {
    return isLoadingRef.current = isDeskBookingLoading || isOfficeUsageLoading || isOfficeViewLoading
    }, [isDeskBookingLoading, isOfficeUsageLoading, isOfficeViewLoading]);

  useEffect(() => {
    dispatch(loadFavouriteOffices());
  }, [dispatch])

  useEffect(() => {
    if (hasOfficeFloorPlan) {
      const deskQueryParam = getInt(getParameterByName('desk', window.location.href) || '');
      if (selectedArea) {
        setTimeout(() => {
          // @ts-ignore
          myRef?.current?.zoomToElement(`area-${selectedArea.id}`, 1);
        }, 250);
      } else if (deskQueryParam) {
        setTimeout(() => {
          // @ts-ignore
          myRef?.current?.zoomToElement(`desk-${deskQueryParam}`, 1);
        }, 1000);
      }
    }
  }, [hasOfficeFloorPlan, selectedArea]);

  const officeDesks = useMemo(() => {
    const desksOnPlan = (officeFloorPlan?.desks ?? []);
    return desks.map((desk: Desk) => {
      const deskFromPlan = (desksOnPlan.find((p: any) => p.deskId === desk.id) ?? {});
      const deskEdit = deskEdits[desk.id];
      return {
        ...deskFromPlan,
        ...desk,
        deskId: desk.id,
        isAmBooked: !!(reservations.find((r: any) => r.amDeskId === desk.id)),
        isPmBooked: !!(reservations.find((r: any) => r.pmDeskId === desk.id)),
        x: deskEdit?.x ?? (desk.x || deskFromPlan.x),
        y: deskEdit?.y ?? (desk.y || deskFromPlan.y),
        width: desk.width || deskFromPlan.width,
        height: desk.height || deskFromPlan.height,
        rotation: deskEdit?.rotation ?? (desk.rotation || deskFromPlan.rotate || 0),
        amReservation: (reservations.find((r: DeskReservations) => r.amDeskId === desk.id) ?? {}),
        pmReservation: (reservations.find((r: DeskReservations) => r.pmDeskId === desk.id) ?? {}),
      }
    });
  }, [officeFloorPlan, desks, reservations, deskEdits]);

  const officeAreas = useMemo(() => {
    const planAreas = officeFloorPlan?.areas ?? [];

    const entityAreas = allOffices
      .filter((o: OfficeEntity) => o.area && o.parentId === currentOffice?.id)
      .map((o: OfficeEntity) => {
        // An unsaved edit can supply the rectangle for an area that has none stored yet
        const areaEdit = areaEdits[o.id];
        const x = areaEdit?.areaX ?? o.areaX;
        const y = areaEdit?.areaY ?? o.areaY;
        const width = areaEdit?.areaWidth ?? o.areaWidth;
        const height = areaEdit?.areaHeight ?? o.areaHeight;
        if (x == null || y == null || width == null || height == null) return undefined;
        return {
          x: x,
          y: y,
          width: width,
          height: height,
          id: o.id,
          label: o.label,
          pendingLabel: areaEdit?.label,
          officeId: o.id,
          landscape: false,
        };
      })
      .filter((a: any) => !!a);

    // Areas added in this session that have not been created in the backend yet
    const pendingNewAreas = Object.keys(areaEdits)
      .map((id: string) => ({id: parseInt(id), edit: areaEdits[parseInt(id)]}))
      .filter(({edit}) => edit.isNew)
      .map(({id, edit}) => ({
        x: edit.areaX ?? 0,
        y: edit.areaY ?? 0,
        width: edit.areaWidth ?? 0,
        height: edit.areaHeight ?? 0,
        id: id,
        label: edit.label ?? '',
        pendingLabel: edit.label,
        officeId: id,
        landscape: false,
        isNew: true,
      }));

    const entityAreaIds = new Set(entityAreas.map((a: any) => a.officeId));
    const remainingPlanAreas = planAreas.filter((a: any) => !entityAreaIds.has(a.officeId));

    return [...remainingPlanAreas, ...entityAreas, ...pendingNewAreas];
  }, [officeFloorPlan, allOffices, currentOffice, areaEdits]);

  useEffect(() => {
    if (isLoadingRef.current) {
      return;
    }

    if (!selectedDate) {
      return;
    }

    if (!currentOffice) {
      return;
    }

    if (!currentOffice.officePlan && !currentOffice.officeFloorPlan) {
      console.error('No office plan for', currentOffice);
      return;
    }

    if (currentOffice?.floorPlanEnabled) {
      if (currentOffice.officePlan) {
        setHasOfficeFloorPlan(true);
        getOfficeLayout(currentOffice.officePlan).then(plan => dispatch(setOfficeFloorPlan(plan)));
      } else if (currentOffice.officeFloorPlan) {
        setHasOfficeFloorPlan(true);
      }
    }

    if (currentOffice?.deskBookingEnabled) {
      dispatch(fetchDesks(currentOffice?.id));
      dispatch(fetchDeskReservations({officeId: currentOffice?.id, date: selectedDate}));
    } else {
      dispatch(clearDesks());
    }
  }, [dispatch, currentOffice, selectedDate, isDeskBookingLoading, isOfficeUsageLoading, isOfficeViewLoading]);

  const onDeskSpotSelected = (deskSpot: any) => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    if (isManageDesksActive) {
      if (selectedDesksForManageDesks.find(id => id === deskSpot.id)) {
        dispatch(removeSelectedDeskId(deskSpot.id));
      } else {
        dispatch(addSelectedDeskId(deskSpot.id));
      }
    } else if (deskSpot?.active === false) {
      dispatch(warningNotification('That desk is not active'));
    } else {
      if (deskSpot?.canUserBook === undefined || deskSpot?.canUserBook) {
        if (props.onDeskSelect || props.onOfficeSelect) {
          if (deskSpot.id && props.onDeskSelect) {
            props.onDeskSelect(deskSpot);
          } else if (deskSpot.officeId && props.onOfficeSelect) {
            props.onOfficeSelect(deskSpot)
          }
        } else {
          setActiveDeskSpot(deskSpot);
        }
      } else {
        dispatch(warningNotification('You cannot book that desk'));
      }
    }
  }

  const clearSelection = () => {
    setActiveDeskSpot(undefined);
    if (isDeskMoveEnabled) {
      dispatch(selectArea(undefined));
    }
  }

  // Published so the sidebar can drop a new area into the middle of the current view
  const publishViewport = (ref: any) => {
    const transformState = ref?.instance?.transformState ?? ref?.state;
    const wrapper = ref?.instance?.wrapperComponent;
    if (!transformState || !wrapper) return;
    dispatch(setMapViewport({
      positionX: transformState.positionX,
      positionY: transformState.positionY,
      scale: transformState.scale,
      width: wrapper.offsetWidth,
      height: wrapper.offsetHeight,
    }));
  }

  const editFloorPlan = () => {
    if (editFloorPlanButtonEnabled && currentOffice) {
      window.location.href = `/edit-floor-plan?officeId=${currentOffice.id}`;
    }
  }

  const toggleManageDesks = () => {
    if (selectedSidebarMode === SidebarMode.ManageDesks) {
      if (pendingDeskEditCount > 0 && !window.confirm(t('floor-plan-editor.unsaved-changes'))) {
        return;
      }
      dispatch(setSidebarMode(SidebarMode.ChooseOffice));
    } else {
      dispatch(setSidebarMode(SidebarMode.ManageDesks));
    }
  }

  const overrideExistedAtMouseDownRef = useRef(false);

  const onDeskMouseDown = (deskSpot: any) => {
    if (!isDeskMoveEnabled) return;
    dragDistanceRef.current = 0;
    overrideExistedAtMouseDownRef.current = !!deskEdits[deskSpot.id];
    dispatch(startDeskMove({id: deskSpot.id, x: deskSpot.x, y: deskSpot.y}));
  }

  const onAreaMouseDown = (areaSpot: any) => {
    if (!isDeskMoveEnabled) return;
    dragDistanceRef.current = 0;
    overrideExistedAtMouseDownRef.current = !!areaEdits[areaSpot.officeId];
    dispatch(startAreaMove({id: areaSpot.officeId, x: areaSpot.x, y: areaSpot.y}));
  }

  const onAreaResize = (areaSpot: any, width: number, height: number) => {
    dispatch(resizeArea({id: areaSpot.officeId, width: width, height: height}));
  }

  const onDeskDragMouseMove = (e: any) => {
    const nowX = e.clientX;
    const nowY = e.clientY;
    const diffX = nowX - dragLastMousePos.current.x;
    const diffY = nowY - dragLastMousePos.current.y;
    if (draggingDeskId !== undefined || draggingAreaId !== undefined) {
      e.stopPropagation();
      // @ts-ignore
      const scale = myRef?.current?.instance?.transformState?.scale ?? 1;
      if (draggingDeskId !== undefined) {
        dispatch(moveDeskBy({dx: diffX / scale, dy: diffY / scale}));
      } else {
        dispatch(moveAreaBy({dx: diffX / scale, dy: diffY / scale}));
      }
      dragDistanceRef.current += Math.abs(diffX) + Math.abs(diffY);
      if (dragDistanceRef.current > 5) {
        didDragRef.current = true;
      }
    }
    dragLastMousePos.current = {x: nowX, y: nowY};
  }

  const onDeskDragMouseUp = () => {
    if (draggingDeskId !== undefined) {
      if (dragDistanceRef.current <= 5 && !overrideExistedAtMouseDownRef.current) {
        dispatch(revertDeskMove(draggingDeskId));
      }
      dispatch(endDeskMove());
    }
    if (draggingAreaId !== undefined) {
      if (dragDistanceRef.current <= 5 && !overrideExistedAtMouseDownRef.current) {
        dispatch(revertAreaMove(draggingAreaId));
      }
      dispatch(endAreaMove());
    }
  }

  const onBackgroundClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (isManageDesksActive) {
      dispatch(clearSelectedDeskIds());
    }
  }

  if (!hasOfficeFloorPlan) {
    return null;
  }


  return (
    <>
      <Container onClick={(e: any) => onBackgroundClick(e)}>
        {isLoading && <LoadingSpinner fullScreen={true} />}
        {!!currentOffice ?
        <>
          <OfficeFloorMapWrapper isInTeams={isInTeams()}>
            <TransformWrapper centerOnInit={officeFloorPlan?.centerOnInit ?? true} initialScale={officeFloorPlan?.initialScale ?? 0.5}
                              maxScale={officeFloorPlan?.maxScale ?? 2} minScale={officeFloorPlan?.minScale ?? 0.5} ref={myRef}
                              panning={{disabled: draggingDeskId !== undefined || draggingAreaId !== undefined}}
                              onInit={publishViewport}
                              onPanningStop={publishViewport}
                              onZoomStop={publishViewport}>
              {(params: any) => (
                <div style={{position: 'relative', height: '100%', width: '100%'}}
                     onClick={clearSelection}
                     onMouseMove={isDeskMoveEnabled ? onDeskDragMouseMove : undefined}
                     onMouseUp={isDeskMoveEnabled ? onDeskDragMouseUp : undefined}>
                  <MapButtonRow>
                    <ToggleableOutlineButton click={() => setActivePeriod(Period.AllDay)} text="button.all-day" isActive={activePeriod === Period.AllDay}/>
                    <ToggleableOutlineButton click={() => setActivePeriod(Period.AM)} text="button.am-only" isActive={activePeriod === Period.AM}/>
                    <ToggleableOutlineButton click={() => setActivePeriod(Period.PM)} text="button.pm-only" isActive={activePeriod === Period.PM}/>
                    <MapControlsContainer className="tools">
                      {editFloorPlanButtonEnabled && <MapControlButton colour={Colours.red} onClick={() => editFloorPlan()}><Icon color={'red'} icon={IconTypes.Chair} /></MapControlButton>}
                      {manageDesksButtonEnabled && <MapControlButton onClick={() => toggleManageDesks()}><Icon icon={IconTypes.Edit} /></MapControlButton>}
                      <MapControlButton onClick={() => params.zoomIn()}>+</MapControlButton>
                      <MapControlButton onClick={() => params.zoomOut()}>-</MapControlButton>
                      <MapControlButton onClick={() => params.resetTransform()}><BodyVerySmall style={{fontSize: 8}} weight={600}>100%</BodyVerySmall></MapControlButton>
                    </MapControlsContainer>
                  </MapButtonRow>

                  <TransformComponent wrapperClass={'transformComponent'} wrapperStyle={{ maxWidth: "100%", maxHeight: "100%" }}>

                    {currentOffice?.officeFloorPlan ? <>
                      <img src={`data:image/svg+xml,${encodeURIComponent(currentOffice?.officeFloorPlan)}`} alt={"test"} />
                    </> : <>
                      {officeFloorPlan && <FloorPlan src={getFloorPlanUrl(officeFloorPlan.floorPlan)}/>}
                    </>}

                    {selectedDate && officeAreas.length > 0 && officeAreas.map((areaSpot: any, key: number) =>(
                        <OfficeAreaSpotOverlay key={key} onSelect={onDeskSpotSelected}
                                               currentDate={selectedDate}
                                               currentUser={currentUser}
                                               activeSpot={activeSpot}
                                               activePeriod={activePeriod}
                                               allOffices={allOffices}
                                               isAreaEditEnabled={isDeskMoveEnabled}
                                               onAreaMouseDown={onAreaMouseDown}
                                               onAreaResize={onAreaResize}
                                               // @ts-ignore
                                               mapScale={myRef?.current?.instance?.transformState?.scale ?? 1}
                                               areaSpot={areaSpot} />
                    ))}
                    {officeDesks && selectedDate && hasOfficeFloorPlan && officeDesks.map((deskSpot: any, key: number) => (
                      <OfficeDeskSpotOverlay key={key} currentDate={selectedDate}
                                             deskSpot={deskSpot}
                                             activeDeskSpot={activeSpot}
                                             currentUser={currentUser}
                                             activePeriod={activePeriod}
                                             selectedDesksForManageDesks={selectedDesksForManageDesks}
                                             isManageDesksEnabled={isManageDesksActive}
                                             isDeskMoveEnabled={isDeskMoveEnabled}
                                             onDeskMouseDown={onDeskMouseDown}
                                             onSelect={onDeskSpotSelected} />
                    ))}

                    {selectedDate && meetingRooms.map((meetingRoom: any, key: number) => <MeetingRoomSpotOverlay key={key}
                                                                                                                 currentDate={selectedDate}
                                                                                                                 currentUser={currentUser}
                                                                                                                 meetingRoom={meetingRoom} />)}

                    {selectedDate && activeSpot && activeSpot?.id && activeSpot?.deskId && <SingleDeskSpotOverlay currentDate={selectedDate}
                                                                                            activeDeskSpot={activeSpot}
                                                                                            currentUser={currentUser}
                                                                                            activePeriod={activePeriod}
                                                                                            office={currentOffice}
                                                                                            onSelect={onDeskSpotSelected} />}
                  </TransformComponent>
                </div>
              )}
            </TransformWrapper>
          </OfficeFloorMapWrapper>
        </>
        :
        <>
          <SelectionMessage>{t('office-map.select-office')}</SelectionMessage>
        </>}
      </Container>
    </>
  )
}

const OfficeFloorMapWrapper = styled.div<any>`
  width: 100%;
  overflow: hidden;
  background-color: ${Colours.veryLightGrey};
  max-height: 100%;
  .transformComponent {
    width: 100vw;
    height: calc(100vh - ${props => props.isInTeams ? '260px' : '310px'});
    @media (${tablet}) {
      height: calc(100vh - ${props => props.isInTeams ? '125px' : '180px'});
    }
  }
`

const FloorPlan = styled.img`
`

const Container = styled.div`
  display: flex;
  max-height: 100%;
`
